import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft } from "lucide-react";
// Firebase loaded dynamically below to keep initial bundle small
import { sampleArticles } from "@/lib/sample-articles";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SafeImage } from "@/components/ui/SafeImage";
import type { Article } from "@/types";

export function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchArticle() {
      if (!slug) return;
      try {
        const { getArticleBySlug } = await import("@/lib/firebase");
        const data = await getArticleBySlug(slug);
        if (data) {
          setArticle(data);
        } else {
          const sample = sampleArticles.find((a) => a.slug === slug);
          setArticle(sample ?? null);
        }
      } catch {
        const sample = sampleArticles.find((a) => a.slug === slug);
        setArticle(sample ?? null);
      } finally {
        setLoading(false);
      }
    }
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-5 py-20">
        <div className="space-y-6 animate-pulse">
          <div className="h-4 bg-muted rounded w-32 mb-10" />
          <div className="h-12 bg-muted rounded w-3/4 mx-auto" />
          <div className="h-4 bg-muted rounded w-48 mx-auto mt-6" />
          <div className="aspect-[21/9] bg-muted rounded-2xl mt-12 mb-16" />
          <div className="space-y-4 max-w-[65ch] mx-auto">
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-full" />
            <div className="h-4 bg-muted rounded w-5/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="max-w-4xl mx-auto px-5 py-32 text-center">
        <SEO title="Article introuvable" noindex />
        <h1 className="text-3xl font-bold tracking-tight mb-4 text-foreground font-heading">
          Article introuvable
        </h1>
        <p className="text-muted-foreground text-lg mb-8">
          Cet article n'existe pas ou a ete supprime.
        </p>
        <Link
          to="/"
          className="inline-flex items-center justify-center gap-2 h-10 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour a l'accueil
        </Link>
      </div>
    );
  }

  const publishedISO = article.createdAt.toISOString();
  const modifiedISO = article.updatedAt.toISOString();

  return (
    <main className="max-w-4xl mx-auto px-5 py-12 md:py-20">
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/article/${article.slug}`}
        image={article.coverImage || undefined}
        imageAlt={article.title}
        type="article"
        article={{
          publishedTime: publishedISO,
          modifiedTime: modifiedISO,
          author: article.author,
          section: article.category,
          tags: [article.category],
        }}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: article.category, path: "/categories" },
          { name: article.title, path: `/article/${article.slug}` },
        ]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          description: article.excerpt,
          image: article.coverImage || undefined,
          author: {
            "@type": "Person",
            name: article.author,
          },
          publisher: {
            "@type": "Organization",
            name: "Le Cobaye",
            url: "https://le-cobaye.fr",
          },
          datePublished: publishedISO,
          dateModified: modifiedISO,
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://le-cobaye.fr/article/${article.slug}`,
          },
          inLanguage: "fr",
          articleSection: article.category,
          wordCount: article.content.replace(/<[^>]*>/g, "").split(/\s+/).length,
          ...(article.readingTime ? { timeRequired: `PT${article.readingTime}M` } : {}),
        }}
      />

      <Breadcrumbs
        items={[
          { label: "Accueil", href: "/" },
          { label: article.category, href: "/categories" },
          { label: article.title },
        ]}
      />

      <article>
        <header className="text-center max-w-[70ch] mx-auto mb-12">
          <p className="text-sm font-semibold tracking-widest uppercase text-primary mb-6">
            <time dateTime={publishedISO}>
              {format(article.createdAt, "d MMMM yyyy", { locale: fr })}
            </time>
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-foreground mb-8 font-heading">
            {article.title}
          </h1>
          {article.readingTime && (
            <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary/40" />
              {article.readingTime} minutes de lecture
            </p>
          )}
        </header>

        {article.coverImage && (
          <div className="w-full rounded-2xl overflow-hidden mb-16 shadow-lg bg-muted border border-border/40">
            <SafeImage
              src={article.coverImage}
              alt={article.title}
              priority
              className="w-full h-auto object-contain max-h-[600px] mx-auto"
            />
          </div>
        )}

        <div
          className="prose prose-lg dark:prose-invert prose-headings:font-heading prose-p:leading-relaxed mx-auto tiptap"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />
      </article>
    </main>
  );
}
